import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Input } from "@/components/ui/input";
import { Keyboard, ArrowRight, Search, Languages } from "lucide-react";
import { useDragSelect } from "@/lib/useDragSelect";
import { setQueue } from "@/lib/practiceQueue";
import SelectionTray from "@/components/SelectionTray";

const LANGS = [
  { k: "all", label: "All" },
  { k: "en", label: "English" },
  { k: "hi", label: "हिन्दी" },
];
const DIFFS = [
  { k: "all", label: "All levels" },
  { k: "easy", label: "Easy" },
  { k: "medium", label: "Medium" },
  { k: "hard", label: "Hard" },
];

export default function TypingList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [lang, setLang] = useState("all");
  const [diff, setDiff] = useState("all");
  const listRef = useRef(null);
  const { selected, dragging, rect, toggle, clear } = useDragSelect(listRef);

  useEffect(() => {
    api.get("/typing-passages").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return items.filter((p) => {
      if (lang !== "all" && p.language !== lang) return false;
      if (diff !== "all" && p.difficulty !== diff) return false;
      if (query && !p.title.toLowerCase().includes(query)) return false;
      return true;
    });
  }, [items, q, lang, diff]);

  const badge = (d) => ({
    easy: "bg-green-100 text-green-800 border-green-300",
    medium: "bg-yellow-100 text-yellow-800 border-yellow-300",
    hard: "bg-red-100 text-red-800 border-red-300",
  }[d] || "bg-secondary/60 text-foreground border-neutral-300");

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-brand-gold mb-4">Module 04</div>
          <h1 className="text-4xl lg:text-5xl font-black tracking-tight flex items-center gap-4">
            <Keyboard className="w-10 h-10" /> Typing Speed Test
          </h1>
          <p className="mt-3 font-mono text-muted-foreground max-w-2xl">
            English aur हिन्दी dono mein typing speed check karo. Timer 1 se 30 minute tak set kar sakte ho.
          </p>
        </div>

        <div className="sticky top-16 z-30 -mx-6 px-6 py-3 bg-background/95 backdrop-blur border-b border-border/60 mb-8 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70 pointer-events-none" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search titles…"
              className="rounded-lg h-11 pl-9 font-mono border-border"
              data-testid="typing-search"
            />
          </div>
          <div className="inline-flex border border-border" role="tablist">
            {LANGS.map((o, i) => (
              <button
                key={o.k}
                onClick={() => setLang(o.k)}
                data-testid={`typing-lang-${o.k}`}
                className={`px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-bold ${i > 0 ? "border-l border-border" : ""} transition-colors ${
                  lang === o.k ? "bg-brand-blue text-white" : "bg-white text-muted-foreground hover:bg-secondary/60"
                }`}
              >
                <Languages className="w-3 h-3 inline mr-1" /> {o.label}
              </button>
            ))}
          </div>
          <div className="inline-flex border border-border" role="tablist">
            {DIFFS.map((d, i) => (
              <button
                key={d.k}
                onClick={() => setDiff(d.k)}
                data-testid={`typing-diff-${d.k}`}
                className={`px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-bold ${i > 0 ? "border-l border-border" : ""} transition-colors ${
                  diff === d.k ? "bg-brand-gold text-white" : "bg-white text-muted-foreground hover:bg-secondary/60"
                }`}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>

        <div ref={listRef} className="relative select-none" data-testid="typing-list-region">
          {loading ? (
            <div className="font-mono text-muted-foreground">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="border-2 border-dashed border-border p-10 text-center font-mono text-muted-foreground" data-testid="typing-empty">
              {items.length === 0 ? "No passages yet." : "No passages match your filters."}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4" data-testid="typing-list">
              {filtered.map((p) => {
                const isSel = selected.has(p.id);
                return (
                  <div
                    key={p.id}
                    data-testid={`typing-item-${p.id}`}
                    data-select-key={p.id}
                    className={`group relative border p-6 flex flex-col cursor-pointer transition-all ${
                      isSel ? "border-brand-gold ring-2 ring-brand-gold/40 bg-brand-gold/5" : "border-border bg-white hover:border-brand-gold/40 hover:-translate-y-1"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSel}
                      onChange={() => toggle(p.id)}
                      onClick={(e) => e.stopPropagation()}
                      data-noselect
                      data-testid={`typing-check-${p.id}`}
                      aria-label={`Select ${p.title}`}
                      className="absolute top-3 left-3 w-4 h-4 accent-[var(--brand-gold)] cursor-pointer z-10"
                    />
                    <div className="flex items-start justify-between mb-4 ml-6">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-muted-foreground/70">
                          {p.language === "hi" ? "हिन्दी" : "English"} · {p.word_count} words
                        </div>
                        <h3 className="text-xl font-black tracking-tight mt-1">{p.title}</h3>
                      </div>
                      <span className={`text-xs font-mono px-2 py-1 border ${badge(p.difficulty)}`}>{p.difficulty}</span>
                    </div>
                    <div className={`text-sm font-mono text-muted-foreground line-clamp-3 flex-1 ${p.language === "hi" ? "leading-relaxed" : ""}`}>
                      {p.content}
                    </div>
                    <div className="flex items-center justify-between mt-6">
                      <span className="text-xs uppercase tracking-widest font-bold text-muted-foreground">1-30 min timer</span>
                      <Link
                        to={`/typing/${p.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs uppercase tracking-widest font-bold text-brand-gold group-hover:underline underline-offset-4"
                        data-testid={`typing-open-${p.id}`}
                      >
                        Start test <ArrowRight className="w-3.5 h-3.5 inline" />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {dragging && rect && (
            <div
              className="fixed pointer-events-none border-2 border-brand-gold bg-brand-gold/10 rounded"
              style={{ left: rect.left, top: rect.top, width: rect.right - rect.left, height: rect.bottom - rect.top }}
              data-testid="drag-marquee"
            />
          )}
        </div>

        <SelectionTray
          count={selected.size}
          onClear={clear}
          onStart={() => setQueue("typing", Array.from(selected))}
          firstHref={selected.size ? `/typing/${Array.from(selected)[0]}` : "#"}
          moduleLabel="Typing"
        />
      </main>
    </div>
  );
}
